import { Form, Formik, FormikHelpers } from "formik";
import { useState, useEffect, useMemo } from "react";
import { SwitchWithLabel } from "@/core/components/switch-with-label";
import { Button } from "@/core/components/ui/button";
import { Label } from "@/core/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/core/components/ui/radio-group";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { SPIFF_RULE_EVENTS } from "@/spiff-rules/constants";
import { useUpsertSpiffRule } from "@/spiff-rules/query";
import { SpiffRule, SpiffRuleEvent } from "@/spiff-rules/spiff-rule";
import { DirtyListener, DirtyListenerProps } from "./dirty-listener";
import { SpiffRuleDollarInput } from "./spiff-rule-dollar-input";
import {
  spiffRuleValidationSchema,
  SpiffRuleFormValues,
  EMPTY_SPIFF_RULE_FORM_VALUES,
  convertSpiffRuleToFormValues,
  convertSpiffRuleFormValuesToParams,
} from "./spiff-rule-form-values";
import { SpiffRulePercentInput } from "./spiff-rule-percent-input";
import { SpiffRuleText } from "./spiff-rule-text";

const PAYMENT_TYPES = {
  fixedAmount: "fixedAmount",
  percentage: "percentage",
} as const;

type PaymentType = (typeof PAYMENT_TYPES)[keyof typeof PAYMENT_TYPES];

interface SpiffRuleFormProps {
  businessId?: string | null;
  spiffRule?: SpiffRule;
  event: SpiffRuleEvent;
  title: string;
  description: string;
  setFormDirty: DirtyListenerProps["setFormDirty"];
  disabled?: boolean;
  disabledTooltip?: React.ReactNode | string;
}

export const SpiffRuleForm = ({
  businessId,
  spiffRule,
  event,
  title,
  description,
  setFormDirty,
  disabled = false,
  disabledTooltip,
}: SpiffRuleFormProps) => {
  const spiffRulePaymentType = spiffRule?.payment_amount_cents
    ? PAYMENT_TYPES.fixedAmount
    : spiffRule?.payment_percent
      ? PAYMENT_TYPES.percentage
      : undefined;

  const initPaymentType = spiffRulePaymentType ?? PAYMENT_TYPES.fixedAmount;
  const [paymentType, setPaymentType] = useState(initPaymentType);
  useEffect(() => setPaymentType(initPaymentType), [initPaymentType]);

  const initialValues = useMemo<SpiffRuleFormValues>(
    () =>
      spiffRule
        ? convertSpiffRuleToFormValues(spiffRule)
        : EMPTY_SPIFF_RULE_FORM_VALUES,
    [spiffRule],
  );

  const { mutate: upsertSpiffRule } = useUpsertSpiffRule(businessId);

  const handleSubmit = async (
    values: SpiffRuleFormValues,
    { resetForm }: FormikHelpers<SpiffRuleFormValues>,
  ) => {
    if (disabled) return;

    const params = convertSpiffRuleFormValuesToParams(values);

    upsertSpiffRule(
      {
        event,
        ...params,
      },
      {
        onSuccess: () => {
          resetForm({ values });
          toast.success(`${title} rule saved`);
        },
        onError: (error) => {
          toast.error(`${title} rule failed to save`, {
            description:
              error instanceof Error ? error.message : "Please try again",
          });
        },
      },
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={spiffRuleValidationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ dirty, resetForm, isValid, setFieldValue, values }) => (
        <Form className="w-full">
          <DirtyListener setFormDirty={setFormDirty} />

          <Stack>
            <Stack direction="row" justify="between" fullWidth>
              <SpiffRuleText title={title} description={description} />

              <SwitchWithLabel
                label="Enable program"
                checked={values.active}
                onChange={(checked) => setFieldValue("active", checked)}
                variant="wideTrack"
                disabled={disabled}
                disabledTooltip={disabled && disabledTooltip}
                labelClassName="whitespace-nowrap"
              />
            </Stack>

            {event === SPIFF_RULE_EVENTS.referral_completion && (
              <RadioGroup
                value={paymentType}
                onValueChange={(selectedPaymentType) => {
                  if (disabled) return;
                  setPaymentType(selectedPaymentType as PaymentType);

                  // Note: payment types are mutually exclusive. If a SpiffRule
                  // record exists but doesn't match the selected type, reset
                  // any incompatible form state using the "empty" object (act
                  // as if there were no preexisting record).
                  const baseFormValues =
                    spiffRulePaymentType === selectedPaymentType
                      ? initialValues
                      : EMPTY_SPIFF_RULE_FORM_VALUES;

                  // Update payment_type in form values
                  const formValues = {
                    ...baseFormValues,
                    payment_type: selectedPaymentType as PaymentType,
                  };

                  resetForm({ values: formValues });
                }}
                className="flex gap-6"
                disabled={disabled}
              >
                <Label className="flex gap-2">
                  <RadioGroupItem
                    value={PAYMENT_TYPES.fixedAmount}
                    disabled={disabled}
                  />
                  <Typography>Fixed amount</Typography>
                </Label>
                <Label className="flex gap-2">
                  <RadioGroupItem
                    value={PAYMENT_TYPES.percentage}
                    disabled={disabled}
                  />
                  <Typography>Job percentage</Typography>
                </Label>
              </RadioGroup>
            )}

            {paymentType === "fixedAmount" ? (
              <SpiffRuleDollarInput
                name="payment_amount_dollars"
                placeholder="0"
                required
                disabled={disabled}
                disabledTooltip={disabled && disabledTooltip}
              />
            ) : (
              <Stack direction="row">
                <SpiffRulePercentInput
                  name="payment_percent"
                  placeholder="0"
                  required
                  disabled={disabled}
                  disabledTooltip={disabled && disabledTooltip}
                />
                <SpiffRuleDollarInput
                  name="payment_percent_cap_dollars"
                  placeholder="No limit"
                  disabled={disabled}
                  disabledTooltip={disabled && disabledTooltip}
                />
              </Stack>
            )}

            {dirty && !disabled && (
              <Stack direction="row">
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  onClick={() => {
                    setPaymentType(initPaymentType);
                    resetForm({ values: initialValues });
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={!isValid}>
                  Save
                </Button>
              </Stack>
            )}
          </Stack>
        </Form>
      )}
    </Formik>
  );
};
